import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { MOTION } from './animationUtils';
import { LARYNGOSCOPE_PATH, LARYNGOSCOPE_VIEWBOX } from '../shared/icons/LaryngoscopeIcon';

const asset = (name) => `${process.env.PUBLIC_URL || ''}/splash-assets/${name}`;

// Design canvas (iPhone 14). Everything below is authored in these px and the
// whole canvas is scaled to fit the viewport, so every element keeps the same
// relationship to every other element on every screen size.
const CANVAS_W = 390;
const CANVAS_H = 844;

// Timeline cues (seconds)
const L = 0;            // Logo scene
const H = 1.85;         // Handoff scene (scope launches)
const LAUNCH = H + 0.05;
const LAND = H + 1.05;
const TOTAL_DURATION = 3.85;

const SERIF = "'Merriweather', Georgia, serif";
const BACKGROUND = 'linear-gradient(to right, #1e293b, #334155)';

// Held laryngoscope pose, in canvas px (box of the scope path, same as the icon's box).
const HELD = { left: 108, top: 712, width: 120, rotation: 52 };

const SCOPE_BOX = LARYNGOSCOPE_VIEWBOX;
const SCOPE_ASPECT = SCOPE_BOX.h / SCOPE_BOX.w;
const SCOPE_VIEWBOX = `${SCOPE_BOX.x} ${SCOPE_BOX.y} ${SCOPE_BOX.w} ${SCOPE_BOX.h}`;

// Metallic gray shades for the held (filled) laryngoscope, one per subpath
const SCOPE_SHADES = ['#c3c9ce', '#dde2e6', '#dde2e6', '#8e959c', '#9aa1a8', '#9aa1a8', '#b6bcc2', '#b0b7bd', '#9aa1a8', '#eef2f4'];
const SCOPE_SUBPATHS = LARYNGOSCOPE_PATH.split(/(?=M)/);

const ScopeFilled = () => (
  <svg viewBox={SCOPE_VIEWBOX} style={{ width: '100%', height: '100%', display: 'block' }}>
    {SCOPE_SUBPATHS.map((d, i) => (
      <path key={i} d={d} fill={SCOPE_SHADES[i] || '#b6bcc2'} />
    ))}
  </svg>
);

const ScopeOutline = () => (
  <svg viewBox={SCOPE_VIEWBOX} style={{ width: '100%', height: '100%', display: 'block' }}>
    <path fill="#fff" d={LARYNGOSCOPE_PATH} />
  </svg>
);

/**
 * Where the header icon draws the laryngoscope path, expressed in canvas px.
 *
 * LaryngoscopeIcon renders LARYNGOSCOPE_VIEWBOX into its box with the default
 * preserveAspectRatio (xMidYMid meet). Reproducing that mapping here means the
 * flying copy — which draws the same path — lands on exactly the same pixels,
 * regardless of the icon's CSS size or the device.
 */
const measureTarget = (canvasEl) => {
  const icon = document.querySelector('[data-laryngoscope-target] svg');
  if (!icon || !canvasEl) return null;

  const r = icon.getBoundingClientRect();
  const c = canvasEl.getBoundingClientRect();
  if (!r.width || !c.width) return null;

  const VB = LARYNGOSCOPE_VIEWBOX;
  const u = Math.min(r.width / VB.w, r.height / VB.h);   // screen px per path unit
  const originX = r.left + (r.width - VB.w * u) / 2 - VB.x * u;
  const originY = r.top + (r.height - VB.h * u) / 2 - VB.y * u;

  const s = c.width / CANVAS_W;                            // screen px per canvas px
  return {
    left: (originX + SCOPE_BOX.x * u - c.left) / s,
    top: (originY + SCOPE_BOX.y * u - c.top) / s,
    width: (SCOPE_BOX.w * u) / s,
  };
};

const SplashScreen = ({ onScopeLanded, onComplete }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const targetRef = useRef(null);
  const landedRef = useRef(false);
  // Latest callbacks, so a parent re-render can't restart the timeline
  const callbacksRef = useRef({ onScopeLanded, onComplete });
  callbacksRef.current = { onScopeLanded, onComplete };

  // Fit the design canvas to the real viewport (and refit if it changes)
  useLayoutEffect(() => {
    const el = rootRef.current;
    const update = () => {
      const r = el.getBoundingClientRect();
      setViewport({ w: r.width, h: r.height });
      targetRef.current = null; // re-measure the icon on the next frame
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Drive the timeline
  useEffect(() => {
    let frame;
    let start = null;

    const tick = (timestamp) => {
      if (start === null) start = timestamp;
      const elapsed = (timestamp - start) / 1000;

      // Measure the header icon once we're about to fly (HomeScreen has long since rendered)
      if (elapsed >= LAUNCH && !targetRef.current) {
        targetRef.current = measureTarget(canvasRef.current);
      }
      if (elapsed >= LAND && !landedRef.current) {
        landedRef.current = true;
        callbacksRef.current.onScopeLanded?.();
      }

      setCurrentTime(elapsed);

      if (elapsed < TOTAL_DURATION) {
        frame = requestAnimationFrame(tick);
      } else {
        callbacksRef.current.onComplete?.();
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  const T = currentTime;

  const scale = Math.min(viewport.w / CANVAS_W, viewport.h / CANVAS_H);
  const canvasLeft = (viewport.w - CANVAS_W * scale) / 2;
  const canvasTop = (viewport.h - CANVAS_H * scale) / 2;

  // Wordmark
  const logoIn = MOTION.enter(T, L + 0.12, L + 0.75, 20);
  const wordIn = MOTION.enter(T, L + 0.5, L + 1.05, 14);
  const logoOut = MOTION.exit(T, H - 0.15, H + 0.45, -26);

  // Figure
  const figIn = MOTION.enter(T, L + 0.5, L + 1.1, 46);
  const figOut = MOTION.exit(T, H + 0.1, H + 0.7, 46);
  const handRelease = MOTION.glide(T, H + 0.05, H + 0.28, 1, 0);
  const breathe = Math.sin(T * 2.1) * 2;

  // Splash → Home crossfade
  const splashOp = MOTION.glide(T, H + 0.15, H + 0.95, 1, 0);

  // Laryngoscope: enters with the figure, bobs while held, then glides to the header icon
  const scopeIn = MOTION.enter(T, L + 0.5, L + 1.1, 46);
  const heldK = MOTION.glide(T, H + 0.05, H + 0.3, 1, 0);
  const scopeBreathe = breathe * heldK;
  const morph = MOTION.glide(T, H + 0.15, H + 0.7, 0, 1);
  const scopeSettle = MOTION.glide(T, H + 1.1, H + 1.4, 1, 0);

  const target = targetRef.current;
  const sx = MOTION.glide(T, LAUNCH, LAND, HELD.left, target ? target.left : HELD.left);
  const sy = MOTION.glide(T, LAUNCH, LAND, HELD.top, target ? target.top : HELD.top);
  const sw = MOTION.glide(T, LAUNCH, LAND, HELD.width, target ? target.width : HELD.width);
  const srot = MOTION.glide(T, LAUNCH, LAND, HELD.rotation, 0);
  // No icon to land on (shouldn't happen from the home screen): just fade out with the splash
  const scopeOp = Math.min(scopeIn.opacity, 1) * scopeSettle * (target ? 1 : splashOp);

  return (
    <div
      ref={rootRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        overflow: 'hidden',
        // Once the scope has landed the home screen is live underneath: let
        // touches through while the last of the overlay fades, so an eager
        // first scroll isn't swallowed.
        touchAction: T >= LAND ? 'auto' : 'none',
        pointerEvents: T >= LAND ? 'none' : 'auto',
      }}
    >
      {/* Full-bleed background fills any letterbox around the canvas */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: BACKGROUND,
          opacity: splashOp,
        }}
      />

      {/* Design canvas, scaled to fit */}
      <div
        ref={canvasRef}
        style={{
          position: 'absolute',
          left: canvasLeft,
          top: canvasTop,
          width: CANVAS_W,
          height: CANVAS_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {/* Splash layer: wordmark + figure. Fades out; the scope below it does not. */}
        <div style={{ position: 'absolute', inset: 0, opacity: splashOp }}>
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 84,
              textAlign: 'center',
              color: '#fff',
              opacity: logoIn.opacity * logoOut.opacity,
              transform: `translateY(${logoIn.ty + logoOut.ty}px)`,
            }}
          >
            <div style={{ fontFamily: SERIF, fontWeight: 700, fontSize: 42, lineHeight: 1.22 }}>
              Baylor<br />College of<br />Medicine
            </div>
            <div
              style={{
                width: 54,
                height: 2,
                background: 'rgba(255,255,255,0.45)',
                margin: '22px auto 0',
                opacity: wordIn.opacity,
              }}
            />
            <div
              style={{
                fontFamily: SERIF,
                fontWeight: 700,
                fontSize: 19,
                letterSpacing: 1.2,
                marginTop: 18,
                color: '#c9d8f2',
                opacity: wordIn.opacity,
                transform: `translateY(${wordIn.ty}px)`,
              }}
            >
              Department of Anesthesiology
            </div>
          </div>

          {/* Figure group — mirrored so he faces right (assets face left) */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              opacity: figIn.opacity * figOut.opacity,
              transform: `translateY(${figIn.ty + figOut.ty}px) scaleX(-1)`,
            }}
          >
            <img
              src={asset('anesthesiologist-cut.svg')}
              alt=""
              style={{ position: 'absolute', left: 30, top: 420, width: 370, transform: `translateY(${breathe}px)` }}
            />
            <img
              src={asset('breathing-tube.svg')}
              alt=""
              style={{ position: 'absolute', left: -20, top: 555, width: 120, height: 220, transform: `translateY(${breathe}px) scaleX(-1)` }}
            />
            {/* Thumb/fingertips clipped out and re-drawn in front of the tube */}
            <img
              src={asset('anesthesiologist-cut.svg')}
              alt=""
              style={{
                position: 'absolute',
                left: 30,
                top: 420,
                width: 370,
                clipPath: 'polygon(0px 213px, 26px 213px, 26px 248px, 0px 248px)',
                transform: `translateY(${breathe}px)`,
              }}
            />
          </div>
        </div>

        {/* Laryngoscope: held (filled) → flying → header icon (outline) */}
        <div
          style={{
            position: 'absolute',
            left: sx,
            top: sy,
            width: sw,
            height: sw * SCOPE_ASPECT,
            opacity: scopeOp,
            transform: `translateY(${scopeIn.ty + scopeBreathe}px) rotate(${srot}deg)`,
            transformOrigin: '50% 50%',
          }}
        >
          <div style={{ position: 'absolute', inset: 0, opacity: 1 - morph }}>
            <ScopeFilled />
          </div>
          <div style={{ position: 'absolute', inset: 0, opacity: morph }}>
            <ScopeOutline />
          </div>
        </div>

        {/* Gripping glove, in front of the scope handle; lets go as the scope launches */}
        <img
          src={asset('scope-hand.svg')}
          alt=""
          style={{
            position: 'absolute',
            left: -10,
            top: 421,
            width: 370,
            opacity: figIn.opacity * figOut.opacity * splashOp * handRelease,
            transform: `translateY(${figIn.ty + figOut.ty + breathe}px) scaleX(-1)`,
          }}
        />
      </div>
    </div>
  );
};

export default SplashScreen;
